import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookGuide, BookGuideScope } from '../../types';
import { getGraphQLClient, SAVE_BOOK_GUIDE, DELETE_BOOK_GUIDE } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { AutoResizeTextarea, Button, Card } from '../common';

interface BookGuidesPanelProps {
  bookId: string;
  guides: BookGuide[];
}

const SCOPES: BookGuideScope[] = ['all', 'plan', 'prose'];

// Free-form, book-level guidance the AI follows when writing (premise, rules,
// voice). Deliberately plainer than the structured Story Bible: these are
// hand-written prose docs, repo-authoritative — they live in guides/<slug>.md
// and sync both ways. See docs/book-guides-plan.md.
export default function BookGuidesPanel({ bookId, guides: initial }: BookGuidesPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [guides, setGuides] = useState<BookGuide[]>(initial);
  const [adding, setAdding] = useState(false);

  const save = async (vars: Record<string, unknown>) => {
    const res = await getGraphQLClient().request<{
      saveBookGuide: { bookGuide: BookGuide | null; errors: string[] };
    }>(SAVE_BOOK_GUIDE, { bookId, ...vars });
    return res.saveBookGuide?.bookGuide ?? null;
  };

  const upsert = (g: BookGuide) =>
    setGuides((prev) => {
      const idx = prev.findIndex((x) => x.id === g.id);
      if (idx === -1) return [...prev, g].sort((a, b) => a.slug.localeCompare(b.slug));
      const next = [...prev];
      next[idx] = g;
      return next;
    });

  const onAdd = async (slug: string, title: string, content: string, scope: BookGuideScope) => {
    try {
      const saved = await save({ slug, title, content, scope });
      if (saved) upsert(saved);
      setAdding(false);
    } catch (err) {
      captureException(err);
    }
  };

  const onPatch = async (guide: BookGuide, patch: Partial<BookGuide>) => {
    upsert({ ...guide, ...patch }); // optimistic
    try {
      const saved = await save({ bookGuideId: guide.id, ...patch });
      if (saved) upsert(saved);
    } catch (err) {
      captureException(err);
      upsert(guide); // revert
    }
  };

  const onDelete = async (guide: BookGuide) => {
    const prev = guides;
    setGuides((g) => g.filter((x) => x.id !== guide.id));
    try {
      await getGraphQLClient().request(DELETE_BOOK_GUIDE, { bookGuideId: guide.id });
    } catch (err) {
      captureException(err);
      setGuides(prev); // revert
    }
  };

  const activeCount = guides.filter((g) => g.includeInGeneration).length;

  return (
    <Card padding="md" className="mb-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-800"
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          📚 {t('bookGuides.title', 'Guides')}
        </span>
        <span className="text-xs text-gray-400">
          {guides.length === 0
            ? ''
            : t('bookGuides.summary', {
                active: activeCount,
                total: guides.length,
                defaultValue: '{{active}} of {{total}} feeding the AI',
              })}
        </span>
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-gray-400 leading-relaxed">
            {t(
              'bookGuides.subtitle',
              'Hand-written guidance the AI follows everywhere — your premise, rules, voice. Edit them here or in your repo (guides/…); changes sync both ways.'
            )}
          </p>

          {guides.length === 0 && !adding && (
            <p className="text-xs text-gray-400">
              {t('bookGuides.empty', 'No guides yet. Add a premise or rules sheet to steer the AI.')}
            </p>
          )}

          <ul className="space-y-1.5">
            {guides.map((guide) => (
              <GuideRow
                key={guide.id}
                guide={guide}
                onPatch={(patch) => onPatch(guide, patch)}
                onDelete={() => onDelete(guide)}
              />
            ))}
          </ul>

          {adding ? (
            <AddGuideForm onCancel={() => setAdding(false)} onAdd={onAdd} />
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
              {t('bookGuides.add', '+ Add guide')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function GuideRow({
  guide,
  onPatch,
  onDelete,
}: {
  guide: BookGuide;
  onPatch: (patch: Partial<BookGuide>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(guide.title);
  const [content, setContent] = useState(guide.content);

  const dirty = title !== guide.title || content !== guide.content;

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 text-left text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-indigo-600 truncate"
          title={guide.slug}
        >
          {open ? '▾ ' : '▸ '}
          {guide.title?.trim() || guide.slug}
        </button>

        {/* Scope — which generation steps it rides (token budget) */}
        <select
          value={guide.scope}
          onChange={(e) => onPatch({ scope: e.target.value as BookGuideScope })}
          className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1 py-0.5 text-gray-500"
          title={t('bookGuides.scopeHint', 'Which generation steps this guide rides')}
        >
          {SCOPES.map((s) => (
            <option key={s} value={s}>
              {t(`bookGuides.scope.${s}`, s)}
            </option>
          ))}
        </select>

        {/* Use-in-generation toggle */}
        <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={guide.includeInGeneration}
            onChange={(e) => onPatch({ includeInGeneration: e.target.checked })}
          />
          {t('bookGuides.useInGeneration', 'Use')}
        </label>

        <button
          type="button"
          onClick={onDelete}
          aria-label={t('common.delete', 'Delete')}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {open && (
        <div className="px-2 pb-2 space-y-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('bookGuides.titlePlaceholder', 'Title (e.g. Premise)')}
            className="w-full text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-1"
          />
          <AutoResizeTextarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            minRows={4}
            maxRows={20}
            placeholder={t('bookGuides.contentPlaceholder', 'The guidance, in plain prose…')}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || !content.trim()}
              onClick={() => onPatch({ title, content })}
            >
              {t('common.save', 'Save')}
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTitle(guide.title);
                  setContent(guide.content);
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <span className="text-[10px] text-gray-300 dark:text-gray-600 ml-auto">
              guides/{guide.slug}.md
            </span>
          </div>
        </div>
      )}
    </li>
  );
}

function AddGuideForm({
  onAdd,
  onCancel,
}: {
  onAdd: (slug: string, title: string, content: string, scope: BookGuideScope) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [scope, setScope] = useState<BookGuideScope>('all');

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 60) || 'guide';

  return (
    <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-2 space-y-2">
      <input
        type="text"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('bookGuides.titlePlaceholder', 'Title (e.g. Premise)')}
        className="w-full text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-1"
      />
      <AutoResizeTextarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        minRows={3}
        maxRows={16}
        placeholder={t('bookGuides.contentPlaceholder', 'The guidance, in plain prose…')}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
      />
      <div className="flex items-center gap-2">
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as BookGuideScope)}
          className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1 py-0.5 text-gray-500"
        >
          {SCOPES.map((s) => (
            <option key={s} value={s}>
              {t(`bookGuides.scope.${s}`, s)}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="sm"
          disabled={!title.trim() || !content.trim()}
          onClick={() => onAdd(slugify(title), title.trim(), content.trim(), scope)}
        >
          {t('common.add', 'Add')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}
